// ─── TransitMonitor ─────────────────────────────────────────────────
// Janela flutuante no mapa com as OT EM TRÂNSITO, cor e classificação em
// tempo real. Módulo `transit_monitor` (filho de Planeamento). Classificação
// ADAPTATIVA: SLA onde há (assistência), ETA+GPS no resto. Reutiliza
// /operations-summary + /sla-dashboard + posições ao vivo do Redux.
//
// Tempo real: refresca no evento `ordersNeedRefresh` (disparado pelo WS em
// todas as mudanças de OT/rota) + backstop de 60s. O estado GPS (Parado/A
// caminho/velocidade/distância) reage sozinho às posições do Redux.
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Tooltip, Dialog, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import SpeedIcon from '@material-ui/icons/Speed';
import NearMeIcon from '@material-ui/icons/NearMe';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import FilterListIcon from '@material-ui/icons/FilterList';
import RemoveIcon from '@material-ui/icons/Remove';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SatelliteAltIcon from '@material-ui/icons/SettingsInputAntenna';
import LinkIcon from '@material-ui/icons/Link';
import DescriptionIcon from '@material-ui/icons/Description';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CloseIcon from '@material-ui/icons/Close';
import { AuthContext } from '../../App';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { openLiveWindow } from '../../utils/liveWindow';
import { vehicleIconCategory, getVehicleIconDataUrl, onVehicleIconLoaded } from '../../utils/vehicleIcons';

const POS_KEY = 'transitMonitorPos';
const COLLAPSED_KEY = 'transitMonitorCollapsed';
const MUTE_KEY = 'transitMonitorMute';
const BACKSTOP_MS = 60000;
const REMIND_MS = 2 * 60 * 60 * 1000; // lembrete de 2 em 2h (pedido Move Express)
const REMINDER_KEY = 'transitReminder2h';
const LASTSENT_KEY = 'transitLastSent';
const PROBLEM_SEV = 3;   // filtro "só problemas"
const ALERT_SEV = 4;     // som + destaque (Atrasado / SLA crítico / SLA vencido)
const PA_RADIUS_KM = 0.2; // raio (200 m) para considerar a viatura "no P.A." de descarga

// Classificação → cor + gravidade (sev). Estados operacionais + overlay de SLA.
const CLS = {
  late:      { key: 'late',      label: 'Atrasado',    color: '#dc2626', bg: '#fef2f2', dot: '#dc2626', sev: 4 },
  stopped:   { key: 'stopped',   label: 'Parado',      color: '#b45309', bg: '#fffbeb', dot: '#f59e0b', sev: 3 },
  moving:    { key: 'moving',    label: 'A caminho',   color: '#15803d', bg: '#f0fdf4', dot: '#16a34a', sev: 1 },
  returning: { key: 'returning', label: 'Regresso',    color: '#6b21a8', bg: '#faf5ff', dot: '#8b5cf6', sev: 0 },
  sla_overdue:  { key: 'sla_overdue',  label: 'SLA vencido',  color: '#fff',    bg: '#dc2626', dot: '#dc2626', sev: 5 },
  sla_critical: { key: 'sla_critical', label: 'SLA crítico',  color: '#9a3412', bg: '#fff7ed', dot: '#fb923c', sev: 4 },
  sla_warning:  { key: 'sla_warning',  label: 'SLA atenção',  color: '#92400e', bg: '#fffbeb', dot: '#fcd34d', sev: 2 },
  sla_ok:       { key: 'sla_ok',       label: 'SLA no prazo', color: '#1e40af', bg: '#eff6ff', dot: '#3b82f6', sev: 1 },
};
const SLA_MAP = { overdue: 'sla_overdue', critical: 'sla_critical', warning: 'sla_warning', ok: 'sla_ok' };

function fmtMin(ms) {
  const m = Math.round(Math.abs(ms) / 60000);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}
function distKm(aLat, aLng, bLat, bLng) {
  const a = Number(aLat), b = Number(aLng), c = Number(bLat), d = Number(bLng);
  if (![a, b, c, d].every(Number.isFinite)) return null;
  const R = 6371, toR = x => (x * Math.PI) / 180;
  const dLat = toR(c - a), dLng = toR(d - b);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a)) * Math.cos(toR(c)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Beep curto via Web Audio (sem asset). Tom mais agudo quando é crítico.
let _actx = null;
function beep(critical) {
  try {
    _actx = _actx || new (window.AudioContext || window.webkitAudioContext)();
    const t = _actx.currentTime;
    const o = _actx.createOscillator(), g = _actx.createGain();
    o.connect(g); g.connect(_actx.destination);
    o.type = 'sine';
    o.frequency.value = critical ? 880 : 620;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.14, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    o.start(t); o.stop(t + 0.4);
  } catch { /* browser bloqueou áudio — ignora */ }
}

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes tmPulse': {
      '0%': { boxShadow: '0 0 0 0 rgba(220,38,38,0.5)' },
      '70%': { boxShadow: '0 0 0 8px rgba(220,38,38,0)' },
      '100%': { boxShadow: '0 0 0 0 rgba(220,38,38,0)' },
    },
    '@keyframes tmGlow': {
      '0%,100%': { boxShadow: '0 8px 24px rgba(220,38,38,0.45)' },
      '50%': { boxShadow: '0 8px 30px rgba(220,38,38,0.85)' },
    },
    '@keyframes tmLiveBlink': {
      '0%,100%': { opacity: 1 },
      '50%': { opacity: 0.3 },
    },
    // Bolinha LIVE a piscar quando a viatura chega/sai do P.A. de descarga.
    '@keyframes tmArrive': {
      '0%,100%': { boxShadow: '0 0 0 0 rgba(220,38,38,0.65)' },
      '50%': { boxShadow: '0 0 0 6px rgba(220,38,38,0)' },
    },
    '@keyframes tmDepart': {
      '0%,100%': { boxShadow: '0 0 0 0 rgba(22,163,74,0.65)' },
      '50%': { boxShadow: '0 0 0 6px rgba(22,163,74,0)' },
    },
  },
  // Ancorado ao canto inferior esquerdo, alinhado com a pill "Rotas Gravadas"
  // (que vive em bottom:8). Cresce para cima a partir da base.
  panel: {
    position: 'fixed', left: 12, bottom: 8, zIndex: 720, width: 328, maxWidth: 'calc(100vw - 24px)',
    background: '#fff', borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(15,23,42,0.22), 0 2px 8px rgba(15,23,42,0.10)',
    border: '1px solid rgba(15,23,42,0.08)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '11px 12px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff',
    userSelect: 'none',
  },
  headerIcon: { width: 26, height: 26, borderRadius: 8, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: '0.82rem', fontWeight: 800 },
  count: { fontSize: '0.66rem', fontWeight: 800, background: '#22c55e', color: '#052e16', borderRadius: 999, padding: '1px 8px', minWidth: 18, textAlign: 'center' },
  countBad: { background: '#dc2626', color: '#fff' },
  headBtn: { color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 6, '&:hover': { background: 'rgba(255,255,255,0.16)', color: '#fff' } },
  headBtnOn: { color: '#c3f53c' },
  list: { maxHeight: 380, overflowY: 'auto', padding: 6 },
  row: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px',
    borderRadius: 10, cursor: 'pointer', marginBottom: 3,
    transition: 'background 0.12s ease',
    '&:hover': { background: '#f8fafc' },
  },
  rowAlert: { animation: '$tmPulse 1.4s ease-out 3', background: '#fef2f2' },
  dot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, boxShadow: '0 0 0 3px rgba(0,0,0,0.04)' },
  plate: { fontSize: '0.78rem', fontWeight: 800, fontFamily: '"SF Mono","Fira Code",monospace', color: '#0f172a' },
  dest: { fontSize: '0.72rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: '0.64rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 9, marginTop: 2, flexWrap: 'wrap' },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 3 },
  badge: { fontSize: '0.6rem', fontWeight: 800, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' },
  liveBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
    fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.3,
    padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: '#fff',
    boxShadow: '0 1px 4px rgba(21,128,61,0.4)',
    transition: 'transform 0.12s, box-shadow 0.12s',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px rgba(21,128,61,0.55)' },
  },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'tmLiveBlink 1.3s ease-in-out infinite' },
  // Variantes da bolinha LIVE quando há evento no P.A. de descarga:
  //   chegou → vermelho a pulsar · saiu → verde (mais vivo) a pulsar.
  liveBtnArrived: {
    background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
    boxShadow: '0 1px 4px rgba(185,28,28,0.45)',
    animation: '$tmArrive 1.1s ease-out infinite',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px rgba(185,28,28,0.6)' },
  },
  liveBtnDeparted: {
    background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
    boxShadow: '0 1px 4px rgba(21,128,61,0.5)',
    animation: '$tmDepart 1.1s ease-out infinite',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 10px rgba(21,128,61,0.65)' },
  },
  linkBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    width: 22, height: 22, borderRadius: 7, color: '#334155',
    background: '#f1f5f9', border: '1px solid #e2e8f0',
    transition: 'background 0.12s, color 0.12s',
    '&:hover': { background: '#e0e7ff', color: '#4338ca' },
  },
  eta: { fontSize: '0.62rem', fontWeight: 700, marginTop: 2, whiteSpace: 'nowrap' },
  empty: { padding: '22px 14px', textAlign: 'center', color: '#94a3b8', fontSize: '0.76rem' },
  // Mesma dimensão/posição da pill "Rotas Gravadas" (RoutesBrowserDrawer):
  // canto inferior esquerdo, pequena, não compete com o mapa.
  pill: {
    position: 'fixed', left: 12, bottom: 8, zIndex: 750, cursor: 'pointer', userSelect: 'none',
    display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 8px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff',
    borderRadius: 999, boxShadow: '0 4px 14px rgba(15,23,42,0.32)',
    border: '1px solid rgba(255,255,255,0.08)',
    fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.2,
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(15,23,42,0.42)' },
  },
  pillBad: { animation: '$tmGlow 1.4s ease-in-out infinite' },
  pillCount: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.58rem', fontWeight: 800, padding: '0 6px', height: 14,
    borderRadius: 8, minWidth: 18, background: '#22c55e', color: '#052e16',
  },
  pillCountBad: { background: '#dc2626', color: '#fff' },
}));

// Miniatura da viatura (ícone EcooGPS + matrícula) para o Resumo de posições.
// Reutiliza o mesmo conjunto de ícones 2D do mapa; re-renderiza quando o PNG
// acaba de carregar (data-URI cacheado). Fallback: camião cinzento.
function VehicleThumb({ type, color, plate, mapUrl }) {
  const [, force] = useState(0);
  useEffect(() => onVehicleIconLoaded(() => force(n => n + 1)), []);
  const cat = vehicleIconCategory(type);
  const url = getVehicleIconDataUrl(cat, color) || getVehicleIconDataUrl(cat, 'gray');
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 58 }}>
      <Box style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
        {url
          ? <img src={url} alt="" style={{ maxWidth: 42, maxHeight: 42, objectFit: 'contain' }} />
          : <LocalShippingIcon style={{ fontSize: 28, color: '#94a3b8' }} />}
      </Box>
      <Typography style={{ fontSize: '0.6rem', fontWeight: 800, color: '#475569', maxWidth: 66, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {plate}
      </Typography>
      {mapUrl && (
        <a href={mapUrl} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '0.58rem', fontWeight: 700, color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          📍 Mapa
        </a>
      )}
    </Box>
  );
}

function TransitMonitor({ lang = 'pt' }) {
  const classes = useStyles();
  const { token, modules, clientName } = useContext(AuthContext);
  const dispatch = useDispatch();
  const T = (pt, en) => (lang === 'pt' ? pt : en);
  const vehicles = useSelector(selectVehicles);
  // Módulo de tracking público — sem ele não geramos links para o cliente
  // (fail-open sem lista, igual ao ModuleGate/RouteSummaryDialog).
  const hasPublicTracking = !modules || modules.length === 0 || modules.includes('public_tracking');

  const [summary, setSummary] = useState([]);
  const [fuelSituation, setFuelSituation] = useState([]); // ponto de situação por OT (Move Express)
  const [slaLive, setSlaLive] = useState({});
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; } });
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; } });
  const [onlyProblems, setOnlyProblems] = useState(false);
  // Posição ANCORADA À BASE {left, bottom} — arranca alinhada com a pill
  // "Rotas Gravadas" (bottom-left) mas o operador pode arrastar a partir daí.
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(POS_KEY)); if (s && Number.isFinite(s.left) && Number.isFinite(s.bottom)) return s; } catch {}
    return { left: 12, bottom: 8 };
  });
  const [alertIds, setAlertIds] = useState({}); // { vehicleId: expiresAt } — highlight
  const [, force] = useState(0);
  const drag = useRef(null);
  const prevAlert = useRef({}); // { vehicleId: bool } — para detectar transição
  // Estado no P.A. de descarga por OT: 'arrived' (dentro do raio) | 'departed'
  // (já esteve dentro e saiu). Usado para a bolinha LIVE piscar (vermelho/verde)
  // e alertar que o serviço já aconteceu mas pode faltar concluir.
  const [paState, setPaState] = useState({}); // { key: 'arrived'|'departed' }
  const paPrev = useRef({}); // { key: { inside, everInside } } — deteção de transição
  // Resumo de posições + lembrete de 2h (informar o cliente).
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [reminderOn, setReminderOn] = useState(() => { try { return localStorage.getItem(REMINDER_KEY) === '1'; } catch { return false; } });
  const [lastSent, setLastSent] = useState(() => { try { const n = Number(localStorage.getItem(LASTSENT_KEY)); return Number.isFinite(n) && n > 0 ? n : null; } catch { return null; } });
  const lastReminded = useRef(0);

  // ── Fetch ──
  const load = useCallback(async () => {
    if (!token) return;
    const H = { Authorization: `Bearer ${token}` };
    try {
      const [opR, slaR] = await Promise.all([
        fetch('/planner-api/transport-orders/operations-summary', { headers: H }),
        fetch('/planner-api/reports/sla-dashboard', { headers: H }).catch(() => null),
      ]);
      if (opR.ok) setSummary(await opR.json());
      if (slaR && slaR.ok) {
        const sd = await slaR.json();
        const map = {};
        for (const it of (sd?.live?.items || [])) {
          const k = it.id != null ? String(it.id) : (it.orderNumber || '');
          if (k) map[k] = it.status;
        }
        setSlaLive(map);
      }
    } catch { /* próximo evento/tick tenta de novo */ }
    // Ponto de situação por Ordem de Transporte (Move Express); [] p/ outros.
    try {
      const fr = await fetch('/planner-api/transport-orders/fuel-situation', { headers: H });
      setFuelSituation(fr.ok ? await fr.json() : []);
    } catch { setFuelSituation([]); }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(() => { load(); force(t => t + 1); }, BACKSTOP_MS);
    const onRefresh = () => load(); // WS → tempo real
    window.addEventListener('ordersNeedRefresh', onRefresh);
    return () => { clearInterval(id); window.removeEventListener('ordersNeedRefresh', onRefresh); };
  }, [load]);

  // ── Classificação ──
  const classify = useCallback((v) => {
    const cur = v.currentOT;
    if (v.serviceType === 'assistance' && cur) {
      const st = slaLive[String(cur.id)] || slaLive[cur.orderNumber];
      if (st && SLA_MAP[st]) return CLS[SLA_MAP[st]];
    }
    if (!cur) return CLS.returning;
    if (v.delayed) return CLS.late;
    const live = vehicles.find(x => Number(x.id) === Number(v.vehicleId));
    // A posição vem nested (vehicle.position.speed); fallback flat por robustez.
    const spd = live?.position?.speed ?? live?.speed;
    if (spd != null && spd < 3) return CLS.stopped;
    return CLS.moving;
  }, [slaLive, vehicles]);

  // Linhas com classificação, ordenadas por gravidade.
  const rows = useMemo(() => {
    const base = summary
      .filter(v => v.inTransitOTs > 0 || v.currentOT || v.deliveredOTs < v.totalOTs)
      .map(v => ({ v, c: classify(v) }));
    base.sort((a, b) => (b.c.sev - a.c.sev) || String(a.v.vehiclePlate || '').localeCompare(String(b.v.vehiclePlate || '')));
    return base;
  }, [summary, classify]);

  const problems = rows.filter(r => r.c.sev >= PROBLEM_SEV).length;

  // ── Som + destaque quando ENTRA em estado de alerta (Atrasado/SLA crít/venc) ──
  // Não depende do GPS (evita disparar a cada paragem em semáforo).
  useEffect(() => {
    const now = Date.now();
    const nextAlert = {};
    let critical = false, anyNew = false;
    for (const v of summary) {
      const c = classify(v);
      const isAlert = c.sev >= ALERT_SEV;
      if (isAlert && !prevAlert.current[v.vehicleId]) {
        nextAlert[v.vehicleId] = now + 8000; // pulsa 8s
        anyNew = true;
        if (c.sev >= 5) critical = true;
      }
      prevAlert.current[v.vehicleId] = isAlert;
    }
    // limpa viaturas que saíram
    for (const k of Object.keys(prevAlert.current)) {
      if (!summary.some(v => String(v.vehicleId) === String(k))) delete prevAlert.current[k];
    }
    if (anyNew) {
      setAlertIds(prev => ({ ...prev, ...nextAlert }));
      if (!muted) beep(critical);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, slaLive]);

  // ── Chegada/saída do P.A. de descarga ──
  // Para cada OT em curso com P.A. já preenchido (deliveryLat/Lng), compara a
  // posição ao vivo com o raio do P.A.: dentro = 'arrived' (chegou), e depois
  // de ter estado dentro e sair = 'departed' (saiu → serviço feito, falta
  // concluir). Na transição: toast + som (respeita o silêncio). A deteção só
  // arranca quando o P.A. está preenchido (revelado no carregamento).
  useEffect(() => {
    const nextState = {};
    const alive = new Set();
    for (const v of summary) {
      const cur = v.currentOT;
      if (!cur) continue;
      const key = `${v.vehicleId}:${cur.id ?? cur.orderNumber ?? ''}`;
      alive.add(key);
      // Alvos: P.A. principal (+ 2º P.A. na entrega dividida, se existir).
      const targets = [];
      if (Number.isFinite(Number(cur.deliveryLat)) && Number.isFinite(Number(cur.deliveryLng))) targets.push([Number(cur.deliveryLat), Number(cur.deliveryLng)]);
      if (Number.isFinite(Number(cur.deliveryLat2)) && Number.isFinite(Number(cur.deliveryLng2))) targets.push([Number(cur.deliveryLat2), Number(cur.deliveryLng2)]);
      if (!targets.length) continue; // P.A. ainda não preenchido → sem deteção
      const live = vehicles.find(x => Number(x.id) === Number(v.vehicleId));
      const lp = live?.position || live || {};
      const lat = Number(lp.latitude), lng = Number(lp.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const inside = targets.some(([dlat, dlng]) => { const d = distKm(lat, lng, dlat, dlng); return d != null && d <= PA_RADIUS_KM; });
      const known = paPrev.current[key]; // undefined = primeira observação nesta sessão
      const prev = known || { inside: false, everInside: false };
      const everInside = prev.everInside || inside;
      // Transições → toast + som. SÓ dispara para chaves já conhecidas nesta
      // sessão — na 1ª observação apenas "semeia" o estado (senão, a cada
      // refresh, todas as viaturas já dentro do P.A. dispariam de novo).
      if (known) {
        if (inside && !prev.inside) {
          dispatch(addToast({ type: 'info', severity: 'warning',
            message: T(`🔴 ${v.vehiclePlate || 'Viatura'} chegou ao P.A.`, `🔴 ${v.vehiclePlate || 'Vehicle'} reached the delivery point`),
            detail: cur.deliveryName ? String(cur.deliveryName) : (v.routeName || '') }));
          if (!muted) beep(false);
        } else if (!inside && prev.inside && prev.everInside) {
          dispatch(addToast({ type: 'info', severity: 'success',
            message: T(`🟢 ${v.vehiclePlate || 'Viatura'} saiu do P.A.`, `🟢 ${v.vehiclePlate || 'Vehicle'} left the delivery point`),
            detail: T('Serviço realizado — falta concluir no sistema.', 'Service done — still to be closed in the system.') }));
          if (!muted) beep(false);
        }
      }
      paPrev.current[key] = { inside, everInside };
      if (inside) nextState[key] = 'arrived';
      else if (everInside) nextState[key] = 'departed';
    }
    // Limpa OTs que já não estão em curso
    for (const k of Object.keys(paPrev.current)) if (!alive.has(k)) delete paPrev.current[k];
    setPaState(nextState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, vehicles, muted]);

  // ── Drag (ancorado à base: arrastar mantém o crescimento para cima) ──
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const left = Math.max(6, Math.min(window.innerWidth - 120, drag.current.startLeft + (e.clientX - drag.current.mx)));
    const bottom = Math.max(8, Math.min(window.innerHeight - 60, drag.current.startBottom + (drag.current.my - e.clientY)));
    setPos({ left, bottom });
  }, []);
  const onUp = useCallback(() => {
    if (drag.current) setPos(p => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} return p; });
    drag.current = null;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  }, [onMove]);
  const onDown = (e) => {
    drag.current = { mx: e.clientX, my: e.clientY, startLeft: pos.left, startBottom: pos.bottom };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const persist = (k, val, setter) => { setter(val); try { localStorage.setItem(k, val ? '1' : '0'); } catch {} };

  const focus = (v) => {
    const live = vehicles.find(x => Number(x.id) === Number(v.vehicleId));
    const lp = live?.position || live || {};
    window.dispatchEvent(new CustomEvent('focusVehicle', { detail: { id: v.vehicleId, lat: lp.latitude, lng: lp.longitude } }));
  };

  // Abre o monitor Live da rota numa janela própria — o veículo mexe em tempo
  // real (o mapa do planeamento fica "parado" porque só faz refresh a cada
  // ciclo). É o que o operador precisa para dar a posição ao cliente.
  const openLive = (v, e) => {
    e.stopPropagation();
    if (v.routeId) openLiveWindow(v.routeId, token);
  };

  // Gera + copia o LINK de tracking público da rota (para dar ao cliente).
  // Usa o endpoint já existente POST /tracking/sign/:routeId (token 24h).
  const copyTrackLink = async (v, e) => {
    e.stopPropagation();
    if (!v.routeId) return;
    try {
      const r = await fetch(`/planner-api/tracking/sign/${v.routeId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('sign falhou');
      const d = await r.json();
      const url = `${window.location.origin}/#/track/${v.routeId}?t=${encodeURIComponent(d.token)}`;
      await navigator.clipboard.writeText(url);
      dispatch(addToast({ type: 'info', severity: 'success',
        message: T('Link copiado', 'Link copied'),
        detail: T('Válido 24h. Cola no WhatsApp/email do cliente.', "Valid 24h. Paste into the client's WhatsApp/email.") }));
    } catch {
      dispatch(addToast({ type: 'info', severity: 'warning', message: T('Não consegui gerar o link', 'Could not generate the link') }));
    }
  };

  // Alias da viatura (ex.: "V07") — derivado do nome EcooGPS ("V07 - Volvo…").
  const vehAlias = (vid) => {
    const veh = vehicles.find(x => Number(x.id) === Number(vid));
    if (!veh?.name) return null;
    const first = String(veh.name).split(/\s*[-·]\s*/)[0].trim();
    return first || null;
  };
  const PRODUCT_LABEL = { gasolina_95: 'gasolina 95', gasolina: 'gasolina', diesel: 'gasóleo', gasoleo: 'gasóleo', ga: 'GA' };

  // Texto do PONTO DE SITUAÇÃO de UMA Ordem de Transporte (bloco completo).
  const fuelGroupText = (g) => {
    const t = Date.now();
    const pds = new Date(t).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
    const empresa = clientName || 'MOVE EXPRESS';
    const out = [];
    out.push(`Empresa: ${empresa}`);
    out.push(`PDS: ${pds}`);
    const m3 = g.totalLitres ? Math.round(g.totalLitres / 1000) : null;
    const prod = PRODUCT_LABEL[g.fuelProduct] || g.fuelProduct || '';
    if (m3 != null) out.push(`${m3} m³${prod ? ` de ${prod}` : ''}`);
    out.push(`Ordem de Transporte nº ${g.order}`);
    // P.A. de descarga só é revelado no carregamento; enquanto não houver
    // destino, avisa o operador (senão o "a caminho" nunca vira "chegou").
    if (g.deliveryName) out.push(`Destino: ${g.deliveryName}`);
    else out.push('Destino: ⚠️ por confirmar (P.A. revelado no carregamento)');
    if (g.pickupName || g.deliveryName) out.push(`Percurso: ${g.pickupName || '—'} → ${g.deliveryName || '—'}`);
    out.push('');
    let latestEta = null;
    g.trucks.forEach((tr, i) => {
      const alias = vehAlias(tr.vehicleId);
      const litM3 = tr.litres ? Math.round(tr.litres / 1000) : null;
      const head = `${i + 1}- ${litM3 != null ? `(${litM3} m³) ` : ''}${tr.plate || '—'}${alias ? ` · ${alias}` : ''}`;
      if (tr.status === 'delivered') {
        out.push(`${head} · chegou ao destino`);
      } else {
        const veh = vehicles.find(x => Number(x.id) === Number(tr.vehicleId));
        const lp = veh?.position || {};
        const here = lp.address || (Number.isFinite(Number(lp.latitude)) ? `${Number(lp.latitude).toFixed(5)}, ${Number(lp.longitude).toFixed(5)}` : null);
        // "Chegou ao destino" quando a posição ao vivo está dentro do raio do
        // P.A. de descarga — mesmo sem a descarga estar registada no sistema.
        const dToDest = (Number.isFinite(Number(lp.latitude)) && Number.isFinite(Number(tr.deliveryLat)))
          ? distKm(lp.latitude, lp.longitude, tr.deliveryLat, tr.deliveryLng) : null;
        const atDest = dToDest != null && dToDest <= PA_RADIUS_KM;
        if (atDest) {
          out.push(`${head} · chegou ao destino`);
        } else {
          let etaStr = '';
          if (tr.estimatedArrival) {
            const ms = new Date(tr.estimatedArrival).getTime() - t;
            if (ms > 0) { etaStr = ` · chega ~${fmtMin(ms)}`; const eta = new Date(tr.estimatedArrival); if (!latestEta || eta > latestEta) latestEta = eta; }
          }
          out.push(`${head} · a caminho${etaStr}`);
        }
        out.push(`   📍 ${here || 'posição indisponível'}`);
        // Link do mapa (Google Maps) — o responsável abre no WhatsApp e vê a
        // zona onde está a viatura.
        if (Number.isFinite(Number(lp.latitude)) && Number.isFinite(Number(lp.longitude))) {
          out.push(`   🔗 https://maps.google.com/?q=${Number(lp.latitude).toFixed(5)},${Number(lp.longitude).toFixed(5)}`);
        }
      }
    });
    if (latestEta) { out.push(''); out.push(`Chegada prevista: ${latestEta.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`); }
    return out.join('\n');
  };

  // Viaturas (miniaturas) de UMA ordem — alinhadas com o respectivo bloco.
  const groupVehicles = (g) => {
    const seen = new Set(); const list = [];
    (g.trucks || []).forEach(tr => {
      if (tr.vehicleId == null || seen.has(String(tr.vehicleId))) return;
      seen.add(String(tr.vehicleId));
      const veh = vehicles.find(x => Number(x.id) === Number(tr.vehicleId));
      const lp = veh?.position || {};
      const kmh = lp.speed != null ? Math.round(lp.speed) : null;
      const color = kmh != null && kmh > 3 ? 'green' : 'yellow';
      list.push({ vehicleId: tr.vehicleId, plate: tr.plate || veh?.name || '—', type: veh?.type, color });
    });
    return list;
  };

  // Texto completo (todas as ordens) — para copiar / WhatsApp.
  const buildFuelSituationText = () => fuelSituation.map(fuelGroupText).join('\n\n────────────\n\n');

  // Texto do RESUMO de posições — pronto a colar/enviar ao responsável.
  const buildSummaryText = () => {
    // Move Express: template por Ordem de Transporte.
    if (fuelSituation.length > 0) return buildFuelSituationText();
    const t = Date.now();
    const stamp = new Date(t).toLocaleString(lang === 'pt' ? 'pt-PT' : 'en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    const out = [T(`📍 Posição das viaturas · ${stamp}`, `📍 Vehicle positions · ${stamp}`), ''];
    if (rows.length === 0) { out.push(T('Nenhuma viatura em trânsito.', 'No vehicles in transit.')); return out.join('\n'); }
    for (const { v, c } of rows) {
      const live = vehicles.find(x => Number(x.id) === Number(v.vehicleId));
      const lp = live?.position || live || {}; // posição (nested) com fallback flat
      const cur = v.currentOT;
      const kmh = lp.speed != null ? Math.round(lp.speed) : null;
      const dkm = cur && Number.isFinite(Number(lp.latitude))
        ? distKm(lp.latitude, lp.longitude, cur.deliveryLat, cur.deliveryLng) : null;
      // Chegou ao destino? posição ao vivo dentro do raio do P.A. de descarga.
      const atDest = dkm != null && dkm <= PA_RADIUS_KM;
      const dest = cur?.deliveryName || v.routeName || T('em rota', 'on route');
      // POSIÇÃO ATUAL da viatura (o que o cliente quer): morada GPS; fallback
      // às coordenadas; senão "posição indisponível".
      const here = lp.address
        || (Number.isFinite(Number(lp.latitude)) ? `${Number(lp.latitude).toFixed(5)}, ${Number(lp.longitude).toFixed(5)}` : null);
      // Linha 1: viatura + condutor
      out.push(`🚛 ${v.vehiclePlate || '—'}${v.driverName ? ` — ${v.driverName}` : ''}`);
      // Linha 2: onde está AGORA + estado (+ velocidade)
      const posBits = [atDest ? T('Chegou ao destino', 'At destination') : c.label];
      if (kmh != null) posBits.push(`${kmh} km/h`);
      out.push(`   📍 ${here || T('posição indisponível', 'position unavailable')} · ${posBits.join(' · ')}`);
      // Link do mapa (Google Maps) — abre no WhatsApp/telemóvel a zona da viatura.
      if (Number.isFinite(Number(lp.latitude)) && Number.isFinite(Number(lp.longitude))) {
        out.push(`   🔗 https://maps.google.com/?q=${Number(lp.latitude).toFixed(5)},${Number(lp.longitude).toFixed(5)}`);
      }
      // Linha 3: para onde vai + distância/ETA
      const goBits = [];
      if (dkm != null) goBits.push(dkm < 1 ? `${Math.round(dkm * 1000)} m` : `${dkm.toFixed(1)} km`);
      if (!atDest && c.key === 'moving' && cur?.estimatedArrival) { const ms = new Date(cur.estimatedArrival).getTime() - t; if (ms > 0) goBits.push(T(`chega ~${fmtMin(ms)}`, `ETA ~${fmtMin(ms)}`)); }
      if (c.key === 'late' && cur?.estimatedArrival) goBits.push(T(`atrasado ${fmtMin(t - new Date(cur.estimatedArrival).getTime())}`, `late ${fmtMin(t - new Date(cur.estimatedArrival).getTime())}`));
      // Sem coordenadas de destino não dá para detetar a chegada — avisa.
      if (cur && (!Number.isFinite(Number(cur.deliveryLat)) || !Number.isFinite(Number(cur.deliveryLng)))) goBits.push(T('⚠️ destino por confirmar', '⚠️ destination TBC'));
      out.push(`   → ${dest}${goBits.length ? ` · ${goBits.join(' · ')}` : ''}`);
      out.push('');
    }
    return out.join('\n').trim();
  };

  // Viaturas a mostrar em miniatura no Resumo (únicas, com cor por estado).
  // Move Express → viaturas das OT de combustível (fuelSituation); resto → rows.
  const summaryVehicles = useMemo(() => {
    const seen = new Set(); const list = [];
    const add = (vehicleId, plate) => {
      if (vehicleId == null || seen.has(String(vehicleId))) return;
      seen.add(String(vehicleId));
      const veh = vehicles.find(x => Number(x.id) === Number(vehicleId));
      const lp = veh?.position || {};
      const kmh = lp.speed != null ? Math.round(lp.speed) : null;
      const color = kmh != null && kmh > 3 ? 'green' : 'yellow';
      list.push({ vehicleId, plate: plate || veh?.name || '—', type: veh?.type, color });
    };
    if (fuelSituation.length > 0) {
      fuelSituation.forEach(g => (g.trucks || []).forEach(tr => add(tr.vehicleId, tr.plate)));
    } else {
      rows.forEach(({ v }) => add(v.vehicleId, v.vehiclePlate));
    }
    return list;
  }, [fuelSituation, rows, vehicles]);

  // Link do Google Maps para a posição atual de uma viatura (para o botão
  // "📍 Mapa" na miniatura do Resumo). null se não há GPS válido.
  const vehicleMapUrl = (vehicleId) => {
    const veh = vehicles.find(x => Number(x.id) === Number(vehicleId));
    const lp = veh?.position || {};
    if (Number.isFinite(Number(lp.latitude)) && Number.isFinite(Number(lp.longitude))) {
      return `https://maps.google.com/?q=${Number(lp.latitude).toFixed(5)},${Number(lp.longitude).toFixed(5)}`;
    }
    return null;
  };

  const markSent = () => {
    const t = Date.now();
    setLastSent(t); lastReminded.current = t;
    try { localStorage.setItem(LASTSENT_KEY, String(t)); } catch {}
  };
  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(buildSummaryText());
      dispatch(addToast({ type: 'info', severity: 'success', message: T('Resumo copiado', 'Summary copied'), detail: T('Cola no WhatsApp/email do responsável.', "Paste into the manager's WhatsApp/email.") }));
      markSent();
    } catch {
      dispatch(addToast({ type: 'info', severity: 'warning', message: T('Não consegui copiar', 'Copy failed') }));
    }
  };
  const waSummary = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildSummaryText())}`, '_blank');
    markSent();
  };
  const toggleReminder = () => {
    const next = !reminderOn;
    setReminderOn(next);
    try { localStorage.setItem(REMINDER_KEY, next ? '1' : '0'); } catch {}
    if (next) lastReminded.current = Date.now(); // baseline: 1º lembrete só daqui a 2h
  };

  // Lembrete de 2 em 2h — avisa o operador para enviar a posição ao cliente.
  // O relógio conta a partir do último envio (Copiar/WhatsApp) ou de quando o
  // lembrete foi ligado. Só avisa se houver viaturas em trânsito.
  useEffect(() => {
    if (!reminderOn) return undefined;
    const check = () => {
      if (rows.length === 0) return;
      const base = Math.max(lastSent || 0, lastReminded.current || 0);
      if (Date.now() - base >= REMIND_MS) {
        lastReminded.current = Date.now();
        if (!muted) beep(false);
        dispatch(addToast({ type: 'info', severity: 'info',
          message: T('Hora de informar o cliente', 'Time to update the client'),
          detail: T('Envia a posição das viaturas em trânsito (botão Resumo).', 'Send the in-transit vehicle positions (Summary button).') }));
      }
    };
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderOn, rows.length, lastSent, muted]);

  const now = Date.now();
  const shown = onlyProblems ? rows.filter(r => r.c.sev >= PROBLEM_SEV) : rows;

  if (collapsed) {
    // Pill ARRASTÁVEL: arrastar move (liberta o botão de histórico por baixo);
    // clicar (sem arrastar) expande. Distingue-se pelo limiar de movimento.
    const onPillDown = (e) => {
      const startX = e.clientX, startY = e.clientY;
      const base = { left: pos.left, bottom: pos.bottom };
      let moved = false;
      const move = (ev) => {
        const dx = ev.clientX - startX, dy = ev.clientY - startY;
        if (!moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) moved = true;
        if (moved) setPos({
          left: Math.max(6, Math.min(window.innerWidth - 60, base.left + dx)),
          bottom: Math.max(6, Math.min(window.innerHeight - 40, base.bottom - dy)),
        });
      };
      const up = () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        if (moved) setPos(p => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch {} return p; });
        else persist(COLLAPSED_KEY, false, setCollapsed); // clique → expandir
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    };
    return (
      <Box className={`${classes.pill} ${problems > 0 ? classes.pillBad : ''}`} style={{ left: pos.left, bottom: pos.bottom, cursor: 'grab' }}
        title={T('Arrastar para mover · clicar para abrir', 'Drag to move · click to open')} onMouseDown={onPillDown}>
        <LocalShippingIcon style={{ fontSize: 13 }} />
        {T('Em trânsito', 'In transit')}
        <span className={`${classes.pillCount} ${problems > 0 ? classes.pillCountBad : ''}`}>{problems > 0 ? `${problems}!` : rows.length}</span>
        <KeyboardArrowUpIcon style={{ fontSize: 13, opacity: 0.6 }} />
      </Box>
    );
  }

  return (
    <Box className={classes.panel} style={{ left: pos.left, bottom: pos.bottom }}>
      <Box className={classes.header} style={{ cursor: 'grab' }} onMouseDown={onDown}>
        <Box className={classes.headerIcon}><LocalShippingIcon style={{ fontSize: 15 }} /></Box>
        <Typography className={classes.title}>{T('Em trânsito', 'In transit')}</Typography>
        <span className={`${classes.count} ${problems > 0 ? classes.countBad : ''}`}>{rows.length}</span>
        <Box style={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title={T('Resumo de posições (para enviar ao cliente)', 'Positions summary (to send to the client)')} arrow>
            <span className={`${classes.headBtn} ${reminderOn ? classes.headBtnOn : ''}`} onClick={() => setSummaryOpen(true)}><DescriptionIcon style={{ fontSize: 16 }} /></span>
          </Tooltip>
          <Tooltip title={T('Só problemas', 'Problems only')} arrow>
            <span className={`${classes.headBtn} ${onlyProblems ? classes.headBtnOn : ''}`} onClick={() => setOnlyProblems(v => !v)}><FilterListIcon style={{ fontSize: 16 }} /></span>
          </Tooltip>
          <Tooltip title={muted ? T('Som desligado', 'Sound off') : T('Som ligado', 'Sound on')} arrow>
            <span className={`${classes.headBtn} ${muted ? '' : classes.headBtnOn}`} onClick={() => persist(MUTE_KEY, !muted, setMuted)}>
              {muted ? <NotificationsOffIcon style={{ fontSize: 16 }} /> : <NotificationsActiveIcon style={{ fontSize: 16 }} />}
            </span>
          </Tooltip>
          <Tooltip title={T('Recolher', 'Collapse')} arrow>
            <span className={classes.headBtn} onClick={() => persist(COLLAPSED_KEY, true, setCollapsed)}><RemoveIcon style={{ fontSize: 16 }} /></span>
          </Tooltip>
        </Box>
      </Box>

      {shown.length === 0 ? (
        <Typography className={classes.empty}>
          {rows.length === 0
            ? T('Nenhuma OT em trânsito neste momento.', 'No orders in transit right now.')
            : T('Sem problemas — tudo no prazo. 👍', 'No problems — all on track. 👍')}
        </Typography>
      ) : (
        <Box className={classes.list}>
          {shown.map(({ v, c }) => {
            const cur = v.currentOT;
            const live = vehicles.find(x => Number(x.id) === Number(v.vehicleId));
            const highPrio = cur && (cur.priority === 'high' || Number(cur.priority) >= 2);
            const isAlert = (alertIds[v.vehicleId] || 0) > now;
            // detalhe do tempo
            let eta = null;
            if (c.key === 'late' && cur?.estimatedArrival) eta = T(`atrasado ${fmtMin(now - new Date(cur.estimatedArrival).getTime())}`, `late ${fmtMin(now - new Date(cur.estimatedArrival).getTime())}`);
            else if (c.key === 'moving' && cur?.estimatedArrival) { const ms = new Date(cur.estimatedArrival).getTime() - now; eta = ms > 0 ? T(`chega em ${fmtMin(ms)}`, `in ${fmtMin(ms)}`) : T('a chegar', 'arriving'); }
            const lp = live?.position || live || {};
            const kmh = lp.speed != null ? Math.round(lp.speed) : null;
            const dkm = cur && Number.isFinite(Number(lp.latitude)) ? distKm(lp.latitude, lp.longitude, cur.deliveryLat, cur.deliveryLng) : null;
            // Estado no P.A. de descarga → cor/tooltip da bolinha LIVE.
            const pa = paState[`${v.vehicleId}:${cur?.id ?? cur?.orderNumber ?? ''}`];
            const liveCls = pa === 'arrived' ? classes.liveBtnArrived : pa === 'departed' ? classes.liveBtnDeparted : '';
            const liveTip = pa === 'arrived'
              ? T('No P.A. — serviço a decorrer (concluir quando terminar)', 'At delivery point — service in progress (close when done)')
              : pa === 'departed'
                ? T('Saiu do P.A. — serviço realizado, falta concluir no sistema', 'Left delivery point — service done, still to close')
                : T('Acompanhar ao vivo (posição em tempo real)', 'Track live (real-time position)');
            return (
              <Box key={v.vehicleId} className={`${classes.row} ${isAlert ? classes.rowAlert : ''}`} onClick={() => focus(v)}>
                <span className={classes.dot} style={{ background: c.dot }} />
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={classes.plate}>{v.vehiclePlate || '—'}</span>
                    {highPrio && <PriorityHighIcon style={{ fontSize: 13, color: '#dc2626' }} />}
                    {v.totalOTs > 1 && <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 700 }}>{v.deliveredOTs}/{v.totalOTs}</span>}
                  </Box>
                  <Typography className={classes.dest}>
                    <RoomIcon style={{ fontSize: 12, color: '#cbd5e1' }} />
                    {cur?.deliveryName || v.routeName || T('em rota', 'on route')}
                  </Typography>
                  <Typography className={classes.meta}>
                    {v.driverName && <span className={classes.metaItem}><PersonIcon style={{ fontSize: 11 }} />{v.driverName}</span>}
                    {kmh != null && <span className={classes.metaItem}><SpeedIcon style={{ fontSize: 11 }} />{kmh} km/h</span>}
                    {dkm != null && <span className={classes.metaItem}><NearMeIcon style={{ fontSize: 11 }} />{dkm < 1 ? `${Math.round(dkm * 1000)} m` : `${dkm.toFixed(1)} km`}</span>}
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span className={classes.badge} style={{ background: c.bg, color: c.color }}>{c.label}</span>
                  {eta && <span className={classes.eta} style={{ color: c.color }}>{eta}</span>}
                  {v.routeId && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {hasPublicTracking && (
                        <Tooltip title={T('Copiar link de acompanhamento para o cliente (24h)', 'Copy client tracking link (24h)')} arrow>
                          <span className={classes.linkBtn} onClick={(e) => copyTrackLink(v, e)}>
                            <LinkIcon style={{ fontSize: 13 }} />
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={liveTip} arrow>
                        <span className={`${classes.liveBtn} ${liveCls}`} onClick={(e) => openLive(v, e)}>
                          <span className={classes.liveDot} />
                          <SatelliteAltIcon style={{ fontSize: 12 }} /> {pa === 'arrived' ? T('NO P.A.', 'AT PA') : pa === 'departed' ? T('SAIU', 'LEFT') : 'LIVE'}
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Resumo de posições — texto pronto a enviar ao responsável + lembrete 2h */}
      <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}>
        <Box style={{ padding: '14px 18px', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
          <DescriptionIcon style={{ fontSize: 20 }} />
          <Box style={{ flex: 1 }}>
            <Typography style={{ fontSize: '0.92rem', fontWeight: 800 }}>{T('Resumo de posições', 'Positions summary')}</Typography>
            <Typography style={{ fontSize: '0.68rem', opacity: 0.85 }}>{T('Para informar o responsável do cliente', 'To inform the client manager')}</Typography>
          </Box>
          <span className={classes.headBtn} onClick={() => setSummaryOpen(false)}><CloseIcon style={{ fontSize: 18 }} /></span>
        </Box>

        <Box style={{ padding: 16 }}>
          {/* Card do resumo: texto à esquerda + miniatura(s) da viatura à direita, dentro do mesmo card. */}
          {fuelSituation.length > 0 ? (
            /* Move Express: um cartão por Ordem de Transporte, com a(s) viatura(s)
               dessa ordem ALINHADAS à direita do respectivo bloco. */
            <Box style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fuelSituation.map((g, gi) => {
                const gv = groupVehicles(g);
                return (
                  <Box key={g.order || gi} style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                    padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  }}>
                    <Box style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: '#0f172a', lineHeight: 1.5 }}>
                      {fuelGroupText(g)}
                    </Box>
                    {gv.length > 0 && (
                      <Box style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                        {gv.map(sv => (
                          <VehicleThumb key={sv.vehicleId} type={sv.type} color={sv.color} plate={sv.plate} mapUrl={vehicleMapUrl(sv.vehicleId)} />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, maxHeight: 260, overflowY: 'auto', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Box style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', fontSize: '0.78rem', color: '#0f172a', fontFamily: 'inherit', lineHeight: 1.5 }}>
                {buildSummaryText()}
              </Box>
              {summaryVehicles.length > 0 && (
                <Box style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 12, position: 'sticky', top: 0 }}>
                  {summaryVehicles.map(sv => (
                    <VehicleThumb key={sv.vehicleId} type={sv.type} color={sv.color} plate={sv.plate} mapUrl={vehicleMapUrl(sv.vehicleId)} />
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <Button onClick={copySummary} startIcon={<FileCopyIcon style={{ fontSize: 16 }} />}
              variant="contained" disableElevation
              style={{ textTransform: 'none', fontWeight: 700, background: '#4338ca', color: '#fff', borderRadius: 8 }}>
              {T('Copiar', 'Copy')}
            </Button>
            <Button onClick={waSummary} startIcon={<WhatsAppIcon style={{ fontSize: 18 }} />}
              variant="contained" disableElevation
              style={{ textTransform: 'none', fontWeight: 700, background: '#25D366', color: '#fff', borderRadius: 8 }}>
              {T('WhatsApp', 'WhatsApp')}
            </Button>
          </Box>

          {/* Lembrete de 2 em 2h */}
          <Box style={{ marginTop: 14, padding: '10px 12px', background: reminderOn ? '#f0fdf4' : '#f8fafc', border: `1px solid ${reminderOn ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <AccessTimeIcon style={{ fontSize: 18, color: reminderOn ? '#16a34a' : '#94a3b8' }} />
            <Box style={{ flex: 1 }}>
              <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a' }}>
                {T('Lembrar de 2 em 2h', 'Remind every 2h')}
              </Typography>
              <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
                {lastSent
                  ? T(`Último envio: ${new Date(lastSent).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`, `Last sent: ${new Date(lastSent).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`)
                  : T('Ainda não enviaste nenhum resumo.', 'No summary sent yet.')}
              </Typography>
            </Box>
            <Button onClick={toggleReminder} disableElevation
              variant={reminderOn ? 'contained' : 'outlined'}
              style={{ textTransform: 'none', fontWeight: 700, borderRadius: 8,
                ...(reminderOn
                  ? { background: '#16a34a', color: '#fff' }
                  : { color: '#64748b', borderColor: '#cbd5e1' }) }}>
              {reminderOn ? T('Ligado', 'On') : T('Desligado', 'Off')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default TransitMonitor;
